import { PresenceEnum } from '@/models/ScheduledLessonReport';
import { Toggle } from '@/components/ui/daisy';
import { Colors } from '@/components/ui/types';

type TogglePresenceProps = {
  presence?: PresenceEnum | null;
  disabled?: boolean;
  loading?: boolean;
  onClick?(presence: PresenceEnum): void;
  checkedIcon?: React.ReactNode;
  uncheckedIcon?: React.ReactNode;
};

type ToggleStatus = {
  color: Colors;
  className: string;
  checked: boolean;
  indeterminate: boolean;
};

type ToggleStatusMap = Partial<Record<PresenceEnum, ToggleStatus>> & {
  [PresenceEnum.NONE]: ToggleStatus;
};

const toggleStatus: ToggleStatusMap = {
  [PresenceEnum.ATTENDED]: {
    color: 'success',
    className: 'before:bg-success/20 border-success',
    checked: true,
    indeterminate: false,
  },
  [PresenceEnum.MISSED]: {
    color: 'error',
    className: 'text-error',
    checked: false,
    indeterminate: false,
  },
  [PresenceEnum.NONE]: {
    color: 'default',
    className: '',
    checked: false,
    indeterminate: true,
  },
};

export default function TogglePresence({
  presence,
  disabled = false,
  loading,
  onClick,
  checkedIcon,
  uncheckedIcon,
}: TogglePresenceProps) {
  const currentPresence = presence ?? PresenceEnum.NONE;

  const status =
    toggleStatus[currentPresence] ?? toggleStatus[PresenceEnum.NONE];

  function handleChange(checked: boolean) {
    onClick?.(checked ? PresenceEnum.ATTENDED : PresenceEnum.MISSED);
  }

  return (
    <Toggle
      size="md"
      color={status.color}
      className={status.className}
      checked={status.checked}
      indeterminate={status.indeterminate}
      disabled={disabled || loading}
      onChange={({ target }) => handleChange(target.checked)}
      checkedIcon={checkedIcon}
      uncheckedIcon={uncheckedIcon}
    />
  );
}
